/**
 * Cursor manipulation utilities for contentEditable elements
 * Follows Single Responsibility Principle - only handles cursor/selection operations
 */

export interface CursorPosition {
  position: number;
  isFirst: boolean;
  isLast: boolean;
  hasSelection: boolean;
}

/**
 * Static utility class for cursor manipulation in contentEditable segments
 */
export class CursorUtils {
  private static readonly TEXT_SEGMENT_SELECTOR = '[data-text-segment]';

  /**
   * Get element selector for a specific segment index
   */
  private static getSegmentSelector(segmentIndex: number): string {
    return `[data-segment-index="${segmentIndex}"]${this.TEXT_SEGMENT_SELECTOR}`;
  }

  /**
   * Get the text segment element by index
   */
  private static getTextSegmentElement(segmentIndex: number): HTMLSpanElement | null {
    return document.querySelector(this.getSegmentSelector(segmentIndex));
  }

  /**
   * Get the current cursor position within a text segment
   */
  static getCursorPosition(segmentIndex: number): CursorPosition | undefined {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return undefined;

    const range = selection.getRangeAt(0);
    const startOffset: number = range.startOffset;
    const endOffset: number = range.endOffset;
    const hasSelection: boolean = startOffset !== endOffset;
    const isFirst: boolean = startOffset === 0 && !hasSelection;

    const textSegmentElement = this.getTextSegmentElement(segmentIndex);
    let isLast: boolean = false;

    if (textSegmentElement) {
      const textLength: number = textSegmentElement.textContent?.length || 0;
      isLast = startOffset === textLength && !hasSelection;
    }

    return {
      position: startOffset,
      isFirst,
      isLast,
      hasSelection,
    };
  }

  /**
   * Focus a text segment and place cursor at a specific position
   */
  static focusAtPosition(segmentIndex: number, offset: number): void {
    const textSegmentElement = this.getTextSegmentElement(segmentIndex);
    if (!textSegmentElement) return;

    const range = document.createRange();
    const selection = window.getSelection();

    if (selection && textSegmentElement.firstChild) {
      const textNode = textSegmentElement.firstChild;
      const maxOffset: number = textNode.textContent?.length || 0;
      const safeOffset: number = Math.max(0, Math.min(offset, maxOffset));

      range.setStart(textNode, safeOffset);
      range.setEnd(textNode, safeOffset);
      selection.removeAllRanges();
      selection.addRange(range);
      textSegmentElement.focus();
    } else if (selection) {
      range.setStart(textSegmentElement, 0);
      range.setEnd(textSegmentElement, 0);
      selection.removeAllRanges();
      selection.addRange(range);
      textSegmentElement.focus();
    }
  }

  /**
   * Focus a text segment at the start (position 0)
   */
  static focusAtStart(segmentIndex: number): void {
    this.focusAtPosition(segmentIndex, 0);
  }

  /**
   * Focus a text segment at the end
   */
  static focusAtEnd(segmentIndex: number): void {
    const textSegmentElement = this.getTextSegmentElement(segmentIndex);
    if (!textSegmentElement) return;

    const textLength = textSegmentElement.textContent?.length || 0;
    this.focusAtPosition(segmentIndex, textLength);
  }
}
